import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { jsonLines, type JsonFieldChange, type JsonLine } from '@/utils/jsonDiff'

/** How a line differs from the JSON it is being compared with. */
export type JsonLineTone = 'added' | 'removed' | 'changed' | null

/**
 * A bordered pane with a header, as the JSON and change columns use. It takes
 * its width from whoever lays it out — a horizontal scroller adds `shrink-0`
 * and a width of its own, a grid gives it a column.
 */
export function JsonColumnShell({
  className,
  header,
  children,
}: {
  className?: string
  header: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section
      className={cn(
        'flex h-[70vh] min-w-0 flex-col overflow-hidden rounded-lg border bg-(--ad-surface)',
        className
      )}
    >
      <header className="flex flex-col gap-1 border-b bg-muted/50 px-3 py-2">{header}</header>
      <div className="min-h-0 flex-1 overflow-auto">{children}</div>
    </section>
  )
}

export function JsonValue({ value }: { value: unknown }) {
  if (Array.isArray(value)) return <>[]</>
  if (value && typeof value === 'object') return <>{'{}'}</>
  if (typeof value === 'string')
    return <span className="text-(--ad-syntax-string)">{JSON.stringify(value)}</span>
  if (typeof value === 'number') return <span className="text-(--ad-syntax-number)">{value}</span>
  return <span className="text-(--ad-syntax-literal)">{String(value)}</span>
}

export function JsonLineText({ line }: { line: JsonLine }) {
  const comma = line.comma ? ',' : ''
  const key = line.key !== undefined && (
    <>
      <span className="text-(--ad-syntax-key)">{JSON.stringify(line.key)}</span>
      {': '}
    </>
  )
  if (line.close) return <>{line.close}{comma}</>
  if (line.open) return <>{key}{line.open}</>
  return (
    <>
      {key}
      <JsonValue value={line.value} />
      {comma}
    </>
  )
}

/**
 * Pretty-printed JSON with numbered lines, each tagged with the field it
 * prints so a change list can scroll to it, and lit up by how it differs.
 */
export function JsonPane({
  value,
  tone,
  paneId,
}: {
  value: unknown
  /** Called per line; return null for a line that has not changed. */
  tone: (line: JsonLine) => JsonLineTone
  /** Put on the pane, so a change list can find lines inside this one. */
  paneId?: string
}) {
  const lines = useMemo(() => jsonLines(value), [value])

  return (
    <pre data-json-pane={paneId} className="min-w-max py-2 font-mono text-xs leading-5">
      {lines.map((line, i) => {
        const lineTone = tone(line)
        return (
          <div
            key={i}
            data-path={line.close ? undefined : line.path}
            className={cn(
              'flex pr-3',
              lineTone === 'added' && 'bg-(--ad-success-soft)',
              lineTone === 'removed' && 'bg-(--ad-danger-soft)',
              lineTone === 'changed' && 'bg-(--ad-warning-soft)'
            )}
          >
            <span
              className={cn(
                'w-10 shrink-0 border-l-2 border-transparent pr-3 text-right text-(--ad-text-faint) select-none',
                lineTone === 'added' && 'border-(--ad-success)',
                lineTone === 'removed' && 'border-(--ad-danger)',
                lineTone === 'changed' && 'border-(--ad-warning)'
              )}
            >
              {i + 1}
            </span>
            <span style={{ paddingLeft: `${line.depth * 2}ch` }}>
              <JsonLineText line={line} />
            </span>
          </div>
        )
      })}
    </pre>
  )
}

/** One changed field, as the change column lists them. */
export function JsonChangeRow({
  change,
  onClick,
  disabled,
}: {
  change: JsonFieldChange
  onClick?: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex w-full flex-col gap-1 border-b px-3 py-2 text-left font-mono text-xs hover:bg-muted/50 disabled:cursor-default disabled:hover:bg-transparent"
    >
      <span className="break-all text-(--ad-text-muted)">{change.path}</span>
      {change.before !== undefined && (
        <span className="rounded-sm bg-(--ad-danger-soft) px-1 break-all text-(--ad-danger) line-through">
          {change.before}
        </span>
      )}
      {change.after !== undefined && (
        <span className="rounded-sm bg-(--ad-success-soft) px-1 break-all text-(--ad-success)">
          {change.after}
        </span>
      )}
    </button>
  )
}
